import { type ReactElement, type ReactNode } from 'react';
import type { BoardViewKind } from '@molt/shared';
import { Button } from '@/components/Button';
import { Menu, type MenuSection } from '@/components/Menu';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { LocaleFormattingTranslate } from '@/lib/locale-formatting';
import {
  BOARD_VIEW_OPTIONS,
  boardViewLabel,
  isBoardViewAvailableOnMobile,
  type BoardViewAccounting,
} from '@/lib/view-state';

export interface BoardViewSwitcherProps {
  activeView: BoardViewKind;
  accounting?: Partial<Record<BoardViewKind, BoardViewAccounting>> | undefined;
  busy?: boolean | undefined;
  mobile?: boolean | undefined;
  pinnedViews?: readonly BoardViewKind[] | undefined;
  onViewChange: (view: BoardViewKind) => void;
  onPinnedViewsChange?: ((views: readonly BoardViewKind[]) => void) | undefined;
}

/**
 * FE-80.1 — the `·` join is a LIST SEPARATOR, not a grammar, which is what
 * makes composing these parts legitimate where `lib/dnd.ts` forbids composing a
 * sentence from fragments. Each part below is a whole phrase the catalogue
 * declares end to end; none of them is a clause that another key completes.
 *
 * ⚠ The exclusion part used to be the exception, and it is the defect this
 * slice came for: it was built as `` `${count} ${count === 1 ? 'card' :
 * 'cards'} ${reason}` `` — an English plural rule and an English fragment from
 * `lib/view-state.ts`, assembled here. Now it is one plural key per reason,
 * carrying its own `{count}` and its own noun, so a language whose plural rule
 * is not English's can express it.
 */
function optionLabel(
  t: LocaleFormattingTranslate,
  kind: BoardViewKind,
  accounting: BoardViewAccounting | undefined,
  mobileLimited: boolean,
): string {
  const parts = [boardViewLabel(t, kind)];
  const reason = accounting?.reason;
  const count = accounting?.unrenderableCards.length ?? 0;
  if (reason !== undefined && reason !== null && count > 0) {
    /*
     * ⚠ Spelled out rather than built as `boardView.excluded.${reason}`. The
     * `t('…')` anchor that every routing pin in this repository reads is
     * STRUCTURALLY BLIND to a key assembled by interpolation, so an
     * interpolated key is invisible to the per-file count guard AND to the
     * slot gate — the gate that stands between a catalogue and a render-time
     * crash. Two arms cost two lines and are worth more than the brevity.
     */
    parts.push(
      reason === 'noDueDate'
        ? t('boardView.excluded.noDueDate', { count }, count)
        : t('boardView.excluded.noDates', { count }, count),
    );
  }

  if (mobileLimited) {
    parts.push(t('boardView.option.mobileLimited'));
  }

  return parts.join(' · ');
}

/**
 * FE-81.2 (fan-out 16) — A DECLARATION THAT IS TRUNCATED AWAY HAS DECLARED
 * NOTHING, AND AT 360 CSS px THIS ONE WAS.
 *
 * `Menu.tsx:142` renders every item's label inside
 * `<span className="min-w-0 flex-1 truncate">`, and `truncate` is
 * `overflow:hidden; text-overflow:ellipsis; white-space:nowrap`. That is the
 * right default for a menu row and it is wrong for exactly one row: the option
 * that carries `FR-EXP-032`'s web-only declaration, which is the LONGEST label
 * this menu ever builds and the only one whose whole purpose is to be read.
 *
 * The 360×800 capture is what found it. On the narrowest width the product
 * targets, the frame read:
 *
 *   Table    · available on web and desktop, li…
 *   Timeline · 5 cards without start or due …          ← declaration GONE
 *   Dashboard · available on web and desk…
 *
 * Timeline is the worst case and the likeliest selection: it carries an
 * exclusion count as well, so the count consumed the row and the declaration
 * was clipped off entirely. `FR-EXP-032` exists so the gap is declared IN THE
 * PRODUCT rather than in a help article; a declaration the phone cuts off is
 * the help article again, at the one width where it matters.
 *
 * ⚠ THE FIX IS A NODE, NOT A CLASS ON THE PARENT, because `Menu.tsx` is not in
 * this chunk's file list — and it should not be. Loosening `truncate` for every
 * menu in the product to serve one label would trade a defect here for a
 * ragged row height everywhere else. `MenuItem.label` is typed `ReactNode`, so
 * the caller can decide its own wrapping without the primitive changing at all:
 * `white-space` INHERITS, so `whitespace-normal` on a descendant overrides the
 * `nowrap` the parent set, and the parent's `overflow:hidden` then clips
 * nothing because a wrapped line does not overflow.
 *
 * ⚠ THE TEXT IS BYTE-IDENTICAL AND THAT IS LOAD-BEARING. The accessible name of
 * a menu item is computed from its text content, so wrapping the SAME string in
 * one span leaves the name exactly as it was — `'Timeline · available on web
 * and desktop, limited on mobile'`. Three sibling assertions in
 * `BoardViewSwitcher.test.tsx` and two in `FR-EXP-032.spec.ts` match on that
 * name and none of them was touched. Splitting the parts across two elements
 * would have dropped the ` · ` between them and quietly broken all five.
 *
 * ⚠ Applied ONLY where `mobileLimited` is true. The exclusion count truncating
 * on its own is a different requirement's problem and not this chunk's to
 * decide; uniform rows stay uniform everywhere this requirement is silent.
 */
function wrapDeclaration(label: string, mobileLimited: boolean): ReactNode {
  return mobileLimited ? <span className="whitespace-normal">{label}</span> : label;
}

/**
 * ⚠ TWO keys, `boardView.pin` and `boardView.unpin`, and not one key with a
 * `{action}` slot filled by a routed verb. A verb translated apart from the
 * sentence it sits in is the `palette.ts` shape — the frame agrees with the
 * catalogue and the word inside it does not decline with the frame.
 */
function pinLabel(
  t: LocaleFormattingTranslate,
  kind: BoardViewKind,
  pinned: boolean,
  mobileLimited: boolean,
): string {
  const view = boardViewLabel(t, kind);
  const parts = [pinned ? t('boardView.unpin', { view }) : t('boardView.pin', { view })];
  if (mobileLimited) {
    parts.push(t('boardView.pin.mobileLimited'));
  }
  return parts.join(', ');
}

export function BoardViewSwitcher({
  activeView,
  accounting,
  busy,
  mobile = false,
  pinnedViews = [],
  onViewChange,
  onPinnedViewsChange,
}: BoardViewSwitcherProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const activeLabel = boardViewLabel(t, activeView);
  const busyProps = busy === true ? { busy: true, busyLabel: t('boardView.saving') } : {};
  const pinned = new Set(pinnedViews);
  const sections: MenuSection[] = [
    {
      id: 'views',
      selection: 'radio',
      value: activeView,
      onValueChange: (id) => onViewChange(id as BoardViewKind),
      items: BOARD_VIEW_OPTIONS.map((option) => {
        const mobileLimited = mobile && !isBoardViewAvailableOnMobile(option.kind);
        return {
          id: option.kind,
          label: wrapDeclaration(
            optionLabel(t, option.kind, accounting?.[option.kind], mobileLimited),
            mobileLimited,
          ),
          onSelect: () => undefined,
        };
      }),
    },
    ...(onPinnedViewsChange === undefined
      ? []
      : [
          {
            id: 'pinned-views',
            heading: t('boardView.pinnedHeading'),
            items: BOARD_VIEW_OPTIONS.map((option) => {
              const isPinned = pinned.has(option.kind);
              const mobileLimited = mobile && !isBoardViewAvailableOnMobile(option.kind);
              const nextPinned = pinned.has(option.kind)
                ? pinnedViews.filter((kind) => kind !== option.kind)
                : [...pinnedViews, option.kind];
              return {
                id: `pin-${option.kind}`,
                label: wrapDeclaration(
                  pinLabel(t, option.kind, isPinned, mobileLimited),
                  mobileLimited,
                ),
                keepOpenOnSelect: true,
                onSelect: () => onPinnedViewsChange(nextPinned),
              };
            }),
          },
        ]),
  ];

  return (
    <Menu
      align="start"
      sections={sections}
      trigger={
        <Button
          {...busyProps}
          type="button"
          size="sm"
          variant="secondary"
          className="shrink-0 bg-surface-raised/90"
          aria-label={t('boardView.trigger', { view: activeLabel })}
        >
          {activeLabel}
        </Button>
      }
    />
  );
}
